import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { formatCardDate, displayDeck, cruiseLabel } from '../components/shared/helpers';
import HoldTimer from '../components/shared/HoldTimer';
import { trackEvent, cruiseParams } from '../lib/analytics';

export default function PassengerDetails() {
  const {
    selectedDate, passengers, selectedCruise,
    paxDetails, setPaxDetails,
    agreedTnC, setAgreedTnC,
    holdId, fullName, setFullName,
    fading, go, exitFlow,
  } = useBooking();


  // Passenger 1 is the primary guest: their name is the verified account name
  // (fullName), shown locked below. Seed/keep it in sync so the locked field is
  // never blank and the name flows through to booking_name + guest position 1.
  useEffect(() => {
    if (!fullName) return;
    setPaxDetails(prev => {
      if (!prev.length || prev[0].name === fullName) return prev;
      return prev.map((p, i) => (i === 0 ? { ...p, name: fullName } : p));
    });
  }, [fullName, setPaxDetails]);

  // Passenger 1's name is normally locked to the verified account name — but
  // login doesn't block digits, and a digit fails the ticket-name rule below
  // with no way to fix it on a locked field. If the seeded name contains a
  // digit, unlock the field so the guest can correct it in place (edits write
  // back to fullName so booking_name stays in sync). Sticky for the visit so
  // the field doesn't snap back to locked mid-edit.
  const [pax0NameEditable] = useState(() => paxDetails.length > 0 && /\d/.test(paxDetails[0].name));

  // Pricing derived from the selected cruise
  const isDinner = selectedCruise?.cruiseName === 'Dinner Cruise';
  const priceAdult = selectedCruise?.priceAdult ?? 0;
  const priceChild = selectedCruise?.priceChild ?? 0;
  const adultsSubtotal = passengers.adults * priceAdult;
  const childrenSubtotal = passengers.children * priceChild;
  const grandTotal = adultsSubtotal + childrenSubtotal;

  // Passenger summary string (zero counts hidden)
  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');

  // Passenger field updates + validation
  const updatePax = (index, field, value) => {
    setPaxDetails(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  // Age must fall inside the (non-overlapping) band for the passenger's type.
  const AGE_BANDS = {
    infant: { min: 0,  max: 3,    msg: 'Infant must be 3 or under' },
    child:  { min: 4,  max: 12,   msg: 'Child must be 4–12 years' },
    adult:  { min: 13, max: null, msg: 'Adult must be 13 or older' },
  };
  // Inline age-band error for one passenger, or null. An empty age returns null
  // (it's handled as "incomplete" by paxComplete, not shown as a band error).
  const ageError = (p) => {
    const raw = String(p.age).trim();
    if (raw === '') return null;
    const band = AGE_BANDS[p.type];
    if (!band) return null;
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < band.min) return band.msg;
    if (band.max !== null && n > band.max) return band.msg;
    return null;
  };
  // Name is valid when non-empty and free of digits. Digits are stripped on
  // input below, so the /\d/ check is belt-and-braces for the gate.
  const nameValid = (p) => p.name.trim() !== '' && !/\d/.test(p.name);

  const paxComplete = paxDetails.length > 0 && paxDetails.every(p => {
    if (!nameValid(p)) return false;                              // name: required, no digits
    if (String(p.age).trim() === '' || ageError(p) !== null) return false; // age: required + in band
    if (p.type === 'infant') return true;
    if (p.gender === '') return false;                           // gender: non-infant
    return isDinner ? p.food !== '' : true;                      // food: Dinner non-infant
  });
  const canPay = paxComplete && agreedTnC;

  // Guard — an active hold (and its cruise + date) must be present. A refresh
  // clears in-memory state by design, so fall back to Available Cruises.
  if (!selectedCruise || selectedDate === null || !holdId) return <Navigate to="/availability" replace />;

  return (
    <div className={`w-full max-w-[1200px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Back arrow */}
        <button
          type="button"
          onClick={() => go('/cruise')}
          className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] mb-4"
          aria-label="Go back"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Booking-hold countdown (continues across the T&C detour) */}
        <HoldTimer />

        {/* Compact booking summary — non-editable */}
        <div className="border border-[rgba(255,255,255,0.12)] rounded-lg bg-[rgba(255,255,255,0.04)] px-4 py-3 mb-6 font-dm-sans text-sm">
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5">
            <div className="flex items-center gap-2.5 text-white/80">
              <span>{formatCardDate(selectedDate)}</span>
              <span className="text-white/25">·</span>
              <span>{cruiseLabel(selectedCruise, 'short')}&nbsp;· {displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)}</span>
            </div>
            <span className="hidden md:inline text-white/25">·</span>
            <div className="flex items-center gap-2.5 text-white/80">
              <span>{paxSummary}</span>
              <span className="text-white/25">·</span>
              <span className="text-[#C9A84C] font-medium">Total &#8377;{grandTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Two columns: passenger forms (60%) · pricing + pay (40%, sticky) */}
        <div className="flex flex-col gap-6 md:grid md:grid-cols-[60%_40%] md:gap-8 md:items-start">

          {/* LEFT — passenger forms */}
          <div>
            <h2 className="font-cormorant font-normal text-[1.7rem] md:text-[2rem] leading-tight text-white mb-4">
              Passenger Details
            </h2>
            <div className="flex flex-col gap-4">
              {paxDetails.map((p, idx) => (
                <div key={idx} className="rounded-xl border border-white/[0.09] bg-[#162840] p-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-dm-sans text-[13px] font-semibold text-[#C9A84C] tracking-[0.03em]">{p.label}</span>
                    <span className="font-dm-sans text-[13px] uppercase tracking-[0.13em] font-medium text-white/60 border border-white/15 rounded-full px-2.5 py-1">
                      {p.type === 'adult' ? 'Adult' : p.type === 'child' ? 'Child' : 'Infant'}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2.5">
                    {/* Name — passenger 1 is locked to the verified account name */}
                    <div className="group flex items-center gap-3">
                      <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Full Name</label>
                      {idx === 0 && !pax0NameEditable ? (
                        <div
                          className="flex-1 flex items-center justify-between gap-2 rounded-md bg-white/[0.03] border border-white/[0.07] px-2.5 py-1.5 cursor-not-allowed select-none"
                          title="This name is taken from your verified account and can't be changed"
                        >
                          <span className="font-dm-sans text-[15px] text-white/60 truncate">{p.name}</span>
                          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="shrink-0">
                            <rect x="2.5" y="6" width="9" height="6" rx="1.3" stroke="#C9A84C" strokeWidth="1.2" />
                            <path d="M4.5 6V4.5a2.5 2.5 0 0 1 5 0V6" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                          </svg>
                        </div>
                      ) : (
                        <div className="flex-1 border-b border-white/25 group-focus-within:border-[#C9A84C] pb-1.5 transition-colors duration-200">
                          <input
                            type="text"
                            value={p.name}
                            onChange={e => {
                              const clean = e.target.value.replace(/\d/g, '');
                              if (idx === 0) setFullName(clean);
                              updatePax(idx, 'name', clean);
                            }}
                            className="w-full bg-transparent text-white font-dm-sans text-[15px] outline-none caret-[#D4A843]"
                          />
                        </div>
                      )}
                    </div>
                    {idx === 0 && /\d/.test(p.name) && (
                      <p className="font-dm-sans text-[13px] text-white/55 font-light leading-relaxed pl-[92px] -mt-1">
                        This name can't be used for a ticket — please edit your name (numbers aren't allowed).
                      </p>
                    )}

                    {/* Age */}
                    <div className="group flex items-center gap-3">
                      <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Age</label>
                      <div className="flex-1 border-b border-white/25 group-focus-within:border-[#C9A84C] pb-1.5 transition-colors duration-200">
                        <input type="number" min="0" inputMode="numeric" value={p.age} onChange={e => updatePax(idx, 'age', e.target.value.replace(/\D/g, ''))} className="w-full bg-transparent text-white font-dm-sans text-[15px] outline-none caret-[#D4A843]" />
                      </div>
                    </div>
                    {ageError(p) && (
                      <p className="font-dm-sans text-[13px] text-white/55 font-light leading-relaxed pl-[92px] -mt-1">
                        {ageError(p)}
                      </p>
                    )}

                    {p.type !== 'infant' && (
                      <>
                        {/* Gender */}
                        <div className="flex items-center gap-3">
                          <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Gender</label>
                          <div className="flex-1 flex gap-2">
                            {['Male', 'Female', 'Other'].map(g => (
                              <button
                                key={g}
                                type="button"
                                onClick={() => updatePax(idx, 'gender', g)}
                                className={`flex-1 h-8 rounded-full text-sm font-dm-sans font-medium transition-[background-color,border-color,color] duration-200 ${
                                  p.gender === g
                                    ? 'bg-[#C9A84C] text-[#0A1628]'
                                    : 'bg-transparent border border-white/15 text-white/55 hover:border-white/30'
                                }`}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Food preference — Dinner Cruise only */}
                        {isDinner && (
                          <div className="flex items-center gap-3">
                            <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C] leading-tight">Food Pref</label>
                            <div className="flex-1 flex gap-2">
                              {['Veg', 'Non-Veg', 'Jain'].map(f => (
                                <button
                                  key={f}
                                  type="button"
                                  onClick={() => updatePax(idx, 'food', f)}
                                  className={`flex-1 h-8 rounded-full text-sm font-dm-sans font-medium transition-[background-color,border-color,color] duration-200 ${
                                    p.food === f
                                      ? 'bg-[#C9A84C] text-[#0A1628]'
                                      : 'bg-transparent border border-white/15 text-white/55 hover:border-white/30'
                                  }`}
                                >
                                  {f}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {p.type === 'infant' && (
                      <p className="font-dm-sans text-sm text-white/55 font-light">
                        Lap infant — no seat assigned, no charge
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — pricing + T&C + pay (sticky on desktop) */}
          <div className="flex flex-col gap-6 md:sticky md:top-6">

            {/* Pricing summary */}
            <div>
              <h2 className="font-cormorant font-normal text-[1.7rem] md:text-[2rem] leading-tight text-white mb-4">
                Pricing Summary
              </h2>
              <div className="rounded-xl border border-[#C9A84C] bg-[#162840] p-4">
                <div className="flex items-center justify-between py-1.5">
                  <span className="font-dm-sans text-sm font-light text-white/70">
                    {passengers.adults} {passengers.adults === 1 ? 'Adult' : 'Adults'} &times; &#8377;{priceAdult.toLocaleString()}
                  </span>
                  <span className="font-dm-sans text-sm font-light text-white tabular-nums">&#8377;{adultsSubtotal.toLocaleString()}</span>
                </div>
                {passengers.children > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="font-dm-sans text-sm font-light text-white/70">
                      {passengers.children} {passengers.children === 1 ? 'Child' : 'Children'} &times; &#8377;{priceChild.toLocaleString()}
                    </span>
                    <span className="font-dm-sans text-sm font-light text-white tabular-nums">&#8377;{childrenSubtotal.toLocaleString()}</span>
                  </div>
                )}
                {passengers.infants > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="font-dm-sans text-sm font-light text-white/70">
                      {passengers.infants} {passengers.infants === 1 ? 'Infant' : 'Infants'}
                    </span>
                    <span className="font-dm-sans text-sm font-light text-white/55">Complimentary</span>
                  </div>
                )}
                <div className="h-px bg-[rgba(212,168,67,0.25)] my-3" />
                <div className="flex items-center justify-between">
                  <span className="font-dm-sans text-sm font-medium text-white">Total <span className="text-white/55 font-light">(GST inclusive)</span></span>
                  <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* T&C + Proceed to pay */}
            <div>
              <button
                type="button"
                onClick={() => setAgreedTnC(v => !v)}
                className="flex items-start gap-3 mb-6 text-left w-full"
              >
                <span className={`mt-0.5 w-5 h-5 rounded-[5px] shrink-0 flex items-center justify-center border transition-[background-color,border-color] duration-200 ${
                  agreedTnC ? 'bg-[#C9A84C] border-[#C9A84C]' : 'bg-transparent border-white/25'
                }`}>
                  {agreedTnC && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2.5 6.5L5 9L9.5 3.5" stroke="#0A1628" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="font-dm-sans text-sm font-light text-white/65 leading-relaxed">
                  I have read and agree to the{' '}
                  <span
                    role="link"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); go('/terms'); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); go('/terms'); } }}
                    className="text-[#C9A84C] underline underline-offset-2 hover:text-[#B8973E] cursor-pointer"
                  >
                    Terms &amp; Conditions
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!canPay) return;
                  // Funnel step 4 — party details + T&C done (counts only, never names).
                  trackEvent('passenger_details_completed', cruiseParams(selectedCruise, passengers));
                  go('/payment');
                }}
                disabled={!canPay}
                className={`w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                  canPay
                    ? 'opacity-100 active:scale-[0.98] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] cursor-pointer'
                    : 'opacity-30 cursor-not-allowed'
                }`}
              >
                Proceed to Pay
              </button>
              <p className="font-dm-sans text-center text-sm text-white/55 mt-3 leading-relaxed">
                You will be redirected to our secure payment gateway
              </p>
              <button
                type="button"
                onClick={exitFlow}
                className="block mx-auto mt-4 mb-2 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
              >
                Cancel and return to cruise selection
              </button>
            </div>

          </div>

        </div>
      </div>
  );
}
